import { useCallback, useEffect, useRef, useState } from "react";
import { useLatestRequest } from "../../../../hooks/useLatestRequest";
import { fetchCurrentEntitlementProfile } from "../../api";
import { EntitlementProfile } from "../../types";
import {
  BillingPlan,
  fetchBillingPlans,
  fetchMerchantPlan,
  getMerchantPlanErrorMessage,
} from "../MerchantPlan.api";
import { MERCHANT_PLAN_NOTIFICATION_MESSAGES } from "../MerchantPlan.const";
import { mapApiResponseToUI, MerchantPlanUIState } from "../MerchantPlan.data";

export interface MerchantPlanData {
  plan: MerchantPlanUIState | null;
  billingPlans: BillingPlan[];
  entitlementProfile: EntitlementProfile | null;
  loading: boolean;
  refreshing: boolean;
  error: string | null;
  retry: () => Promise<void>;
  refetch: () => Promise<void>;
}

export const useMerchantPlanData = (): MerchantPlanData => {
  const [plan, setPlan] = useState<MerchantPlanUIState | null>(null);
  const [billingPlans, setBillingPlans] = useState<BillingPlan[]>([]);
  const [entitlementProfile, setEntitlementProfile] = useState<EntitlementProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { run } = useLatestRequest();
  const hasDataRef = useRef(false);
  const sessionRef = useRef(0);

  const fetchData = useCallback(async () => {
    sessionRef.current += 1;
    const session = sessionRef.current;

    if (hasDataRef.current) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      const result = await run((signal) =>
        Promise.all([
          fetchMerchantPlan(),
          fetchBillingPlans(signal),
          fetchCurrentEntitlementProfile(signal),
        ]),
      );
      if (session !== sessionRef.current) return;
      if (result === undefined) return;

      const [planResponse, billingPlansResponse, entitlementResponse] = result;

      if (!entitlementResponse.success || entitlementResponse.http_request_status !== 200) {
        throw new Error(
          entitlementResponse.user_message ||
            entitlementResponse.message ||
            "Failed to fetch entitlement profile",
        );
      }

      setPlan(mapApiResponseToUI(planResponse));
      setBillingPlans(billingPlansResponse);
      setEntitlementProfile(entitlementResponse.data);
      hasDataRef.current = true;
      setError(null);
    } catch (requestError) {
      if (session !== sessionRef.current) return;
      setError(
        getMerchantPlanErrorMessage(
          requestError,
          MERCHANT_PLAN_NOTIFICATION_MESSAGES.FETCH_ERROR,
        ),
      );
    } finally {
      if (session === sessionRef.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [run]);

  const refetch = useCallback(async () => {
    await fetchData();
  }, [fetchData]);

  useEffect(() => {
    void fetchData();
    return () => {
      sessionRef.current += 1;
    };
  }, [fetchData]);

  return { plan, billingPlans, entitlementProfile, loading, refreshing, error, retry: refetch, refetch };
};
